import React, { useEffect, useState, useCallback } from "react";
import Camera from "../components/Camera";
import FileUploader from "../components/FileUploader";
import ReactMarkdown from "react-markdown";
import DiaryEntryModal from "../components/DiaryEntryModal";
import { CancelTaskDialog } from "../components/Planning/components/CancelTaskDialog";
import { SamplesDialog } from "../components/SamplesDialog";
import { CloseTrialDialog } from "../components/CloseTrialDialog";
import { closingTaskName, isTrialClosed } from "../utils/trialClose";
import { SAMPLE_FIELDS, hasSamples, shownSamples, takesSamples } from "../utils/samples";

// The tasks of the activities that take samples (NIT, Germination Test, TGW): "Set samples" on them
const SAMPLE_TASKS = new Set(["A36", "A14", "A39"]);
import SecuredImage from "../components/SecuredImage";
import AttachmentDownloadButton from "../components/AttachmentDownloadButton";
import TrialMap from "../components/TrialMap";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import { ArchivedBadge, ArchivedDataBanner } from "../components/masterdata/ArchiveControls";
import { getUserInfo } from "../user";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../config";
import api, {
  getDiaryAttachments,
  getAttachments,
  getTrialDiaryEntry,
} from "../api";
import { formatDate, formatDisplayDate } from "../utils/dateFormat";
import { enrichTask, sortByCatalog, sortByNumber } from "../utils/taskLogic";
import { getTaskBadge } from "../utils/taskBadge";
import { TaskStatusLegend, TaskStatusPill } from "../components/TaskStatus";
import { formatGps, parseGps } from "../utils/gps";

import { Dialog } from "@headlessui/react";
import { useVanoCodes } from "@/context/VanoCodesContext";
import { ACTIVITY_CATEGORIES } from "@/utils/vanoCodes";
import { CO_LABEL } from "../utils/contractLabel";
import TrialStatusBadge from "../components/TrialStatusBadge";
import TaskDateLines from "../components/TaskDateLines";

/**
 * An activity's value on the trial in words, with its colours: Yes green, To be decided yellow, No a
 * neutral grey (not red: No only means not chosen) and Not set a dashed outline.
 */
function activityValue(value) {
  if (value === true || value === 1 || value === "1") {
    return { word: "Yes", className: "bg-green-100 text-green-800 border border-green-300 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800" };
  }
  if (value === 2 || value === "2") {
    return { word: "To be decided", className: "bg-yellow-100 text-yellow-800 border border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-800" };
  }
  if (value === false || value === 0 || value === "0") {
    return { word: "No", className: "bg-gray-100 text-gray-700 border border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600" };
  }
  return { word: "Not set", className: "bg-transparent text-gray-500 border border-dashed border-gray-400 dark:text-gray-400 dark:border-gray-500" };
}

export default function TrialView() {
  // Safe unconditionally: TrialView only ever renders inside PrivateRoute -> VanoCodesProvider,
  // which gates mounting children until the catalog is fetched and validated - so this can't
  // throw here. Called alongside the other hooks, before any early return below (Rules of Hooks).
  const vanoCodes = useVanoCodes();

  // State for notes modal
  const [noteModal, setNoteModal] = useState({
    open: false,
    field: "",
    value: "",
  });
  const [noteSaving, setNoteSaving] = useState(false);

  // Save a note to the API (/trials/{id}/update with just that note)
  async function handleNoteSave() {
    if (!noteModal.field) return;
    setNoteSaving(true);
    try {
      const updatedTrial = { ...trial, [noteModal.field]: noteModal.value };
      // Only the note: an old trial may hold values the API no longer accepts in other fields
      await axios.post(
        `${config.apiBaseUrl}/trials/${trial.id}/update`,
        { [noteModal.field]: noteModal.value },
        {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }
      );
      setTrial(updatedTrial);
      setNoteModal({ open: false, field: "", value: "" });
    } catch (e) {
      alert("Failed to save note.");
    } finally {
      setNoteSaving(false);
    }
  }
  // All hooks at the top
  const { id } = useParams();
  const navigate = useNavigate();
  // Back goes to the trials list, with the filters it had when the trial was opened from it
  const location = useLocation();
  const backTo = location.state?.from || "/trials";
  const [trial, setTrial] = useState(null);
  // Set samples: the saved numbers shown at once
  const saveSamples = (samples) => setTrial((prev) => ({ ...prev, ...samples }));
  // Close/Reopen the trial ({ askFor: "Final Report" } first asks whether to close it), or null
  const [closeDialog, setCloseDialog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diaryModalOpen, setDiaryModalOpen] = useState(false);
  const [editingDiary, setEditingDiary] = useState(null); // Track which diary is being edited
  const [editDiaryModalOpen, setEditDiaryModalOpen] = useState(false); // Separate modal for editing
  const [loadingDiaryEdit, setLoadingDiaryEdit] = useState(false); // Loading state when fetching full diary
  const [attachmentDeleteOpen, setAttachmentDeleteOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null); // a diary entry an admin is deleting
  const [attachmentModal, setAttachmentModal] = useState({
    open: false,
    attachment: null,
  });
  const [attachmentActionLoading, setAttachmentActionLoading] = useState(false);
  const [editDescription, setEditDescription] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showFileUploaderModal, setShowFileUploaderModal] = useState(false);
  const [diaryAttachmentsModal, setDiaryAttachmentsModal] = useState({
    open: false,
    attachments: [],
    diary: null,
  });
  const [showDiaryCameraModal, setShowDiaryCameraModal] = useState(false);
  const [showDiaryFileUploaderModal, setShowDiaryFileUploaderModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [canDelete, setCanDelete] = useState(false); // only admins may delete a trial
  useEffect(() => {
    getUserInfo().then((u) => setCanDelete(Array.isArray(u?.roles) && u.roles.includes("admin")));
  }, []);
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : "";

  const fetchTrial = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .post(
        `${config.apiBaseUrl}/trials/${id}`,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      )
      .then((res) => {
        setTrial(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data || err.message);
        setLoading(false);
      });
  }, [id, token]);

  // Get current user ID from sessionStorage
  function getCurrentUserId() {
    try {
      const userRaw = sessionStorage.getItem("userInfo");
      if (!userRaw) return null;
      let user;
      try {
        user = JSON.parse(userRaw);
      } catch {
        user = userRaw;
      }
      const userData = user?.data ?? user;
      return userData?.id ?? userData?.userId ?? userData?.userid ?? null;
    } catch (e) {
      return null;
    }
  }

  // Get current user roles (admin check)
  function isAdmin() {
    try {
      const userRaw = sessionStorage.getItem("userInfo");
      if (!userRaw) return false;
      let user;
      try {
        user = JSON.parse(userRaw);
      } catch {
        user = userRaw;
      }
      const roles = user?.data?.roles || user?.roles || [];
      if (Array.isArray(roles)) {
        return roles.includes("admin") || roles.includes("Admin");
      }
      return false;
    } catch {
      return false;
    }
  }

  useEffect(() => {
    fetchTrial();
  }, [fetchTrial]);

  function handleDiarySave(data) {
    setDiaryModalOpen(false);
    fetchTrial();
  }

  // `done.closingTask`: Final Report or Trial Done was just completed - asks whether to close the trial
  function handleEditDiarySave(data, done) {
    setEditDiaryModalOpen(false);
    setEditingDiary(null);
    fetchTrial();
    if (done?.closingTask && !isTrialClosed(trial)) setCloseDialog({ askFor: closingTaskName(done.closingTask) });
  }

  // Handle diary attachment updates
  const handleDiaryAttachmentUpdate = async (diaryId) => {
    try {
      // Fetch updated diary entry with attachments
      if (!trial?.id) throw new Error("Trial not loaded");
      const updatedDiary = await getTrialDiaryEntry(trial.id, diaryId);

      // Update the diary in the trial's diary list
      if (trial?.diary_entries) {
        const updatedDiaries = trial.diary_entries.map((entry) =>
          entry.id === diaryId
            ? { ...entry, attachments: updatedDiary.attachments }
            : entry
        );
        setTrial((prevTrial) => ({
          ...prevTrial,
          diary_entries: updatedDiaries,
        }));
      }

      // Update the editing diary if it's the one being updated
      if (editingDiary?.id === diaryId) {
        setEditingDiary((prevDiary) => ({
          ...prevDiary,
          attachments: updatedDiary.attachments,
        }));
      }

      // Return the attachments for the modal to update its local state
      return updatedDiary.attachments || [];
    } catch (error) {
      console.log(
        "Diary API endpoints not available yet, using fallback approach"
      );
      // For now, just return empty array - the modal will handle adding attachments locally
      throw error; // Let the modal handle this gracefully
    }
  };

  if (loading)
    return (
      <div className="p-8 text-center text-gray-500">Loading trial...</div>
    );
  if (error)
    return (
      <div className="p-8 text-center text-red-600">
        <div className="mb-2 font-bold">Error loading trial</div>
        <pre className="bg-red-100 rounded p-2 text-xs text-left overflow-x-auto">
          {JSON.stringify(error, null, 2)}
        </pre>
        <button
          className="mt-4 px-4 py-2 rounded bg-emerald-600 text-white"
          onClick={() => navigate(backTo)}
        >
          Go Back
        </button>
      </div>
    );
  if (!trial)
    return (
      <div className="p-8 text-center text-gray-500">Trial not found.</div>
    );

  // Only assign after trial is loaded
  const currentUserId = getCurrentUserId();
  const studyDirectorId = trial?.study_director?.id ?? trial?.studydirector;

  const canEditAttachment = true;
  // The trial's position, read whichever way it is stored (utils/gps.js)
  const position = parseGps(trial.PosGPSLatitude, trial.PosGPSLongitude);

  // Split diaries by V_A or va_id type
  const diaries = Array.isArray(trial.diaries)
    ? [...trial.diaries].sort((a, b) => {
      const dateA = new Date(a.donedate || a.date || a.created_at || 0);
      const dateB = new Date(b.donedate || b.date || b.created_at || 0);
      return dateB - dateA;
    })
    : [];

  const getTypeValue = (d) => String(d.V_A ?? d.va_id);
  // Visits by number (00, 01 ... 10), applications by letter (A ... Z, AA ...), activities in the
  // catalog's order - so a new entry is always last in its group. (The date only sets the status;
  // the dashboard orders by date.)
  const visitsDiaries = sortByNumber(diaries.filter((d) => getTypeValue(d) === "1"));
  const applicationsDiaries = sortByNumber(diaries.filter((d) => getTypeValue(d) === "2"));
  const activitiesDiaries = sortByCatalog(diaries.filter((d) => getTypeValue(d) === "8"), vanoCodes);
  // Activities under the old system's headings (Trial/Harvest/Report Activities, then Other)
  const categoryOfVano = new Map(vanoCodes.map((code) => [code.vano, code.category]));
  const activityRows = ACTIVITY_CATEGORIES.flatMap((category) => {
    const entries = activitiesDiaries.filter((d) => (categoryOfVano.get(d.vano) ?? null) === category.key);
    return entries.length ? [{ heading: category.title }, ...entries] : [];
  });

  // Determine if we already have a "full" diary entry (heuristic: check for fields that may be omitted in list view)
  function hasFullDiaryData(entry) {
    if (!entry) return false;
    // If any of these detailed fields exist (even undefined but present) we assume it's full
    const keys = [
      "persons",
      "bbch",
      "timeinterval",
      "vano",
      "delflag",
      "dataonpsion",
      "dataonpaper",
    ];
    return keys.every((k) => Object.prototype.hasOwnProperty.call(entry, k));
  }

  async function handleEditDiary(entry) {
    if (!entry) return;
    setLoadingDiaryEdit(true);
    try {
      let fullEntry = entry;
      if (!hasFullDiaryData(entry) || !entry.attachments) {
        try {
          if (trial?.id) {
            const fetched = await getTrialDiaryEntry(trial.id, entry.id);
            if (fetched && typeof fetched === "object") {
              fullEntry = { ...entry, ...fetched };
            }
          }
        } catch (e) {
          console.warn(
            "Failed to fetch full diary entry, proceeding with partial data",
            e
          );
        }
      }
      if (fullEntry && fullEntry.V_A && !fullEntry.va_id) {
        fullEntry = { ...fullEntry, va_id: fullEntry.V_A };
      }
      setEditingDiary({ ...fullEntry });
      setEditDiaryModalOpen(true);
    } finally {
      setLoadingDiaryEdit(false);
    }
  }

  async function openDiaryAttachments(entry) {
    if (!entry) return;
    let full = entry;
    try {
      if (trial?.id) {
        const fetched = await getTrialDiaryEntry(trial.id, entry.id);
        if (fetched && typeof fetched === "object")
          full = { ...entry, ...fetched };
      }
    } catch (e) {
      console.warn("Could not fetch full diary attachments", e);
    }
    setDiaryAttachmentsModal({
      open: true,
      diary: full,
      attachments: full.attachments || [],
    });
  }

  async function refreshDiaryAttachments(diary) {
    if (!diary) return;
    try {
      if (!trial?.id) return;
      const fetched = await getTrialDiaryEntry(trial.id, diary.id);
      const newAtts = fetched?.attachments || [];
      setDiaryAttachmentsModal((modal) => ({ ...modal, attachments: newAtts }));
      // Update attachment count in main trial diary list
      setTrial((prev) => {
        if (!prev?.diaries) return prev;
        return {
          ...prev,
          diaries: prev.diaries.map((d) =>
            d.id === diary.id ? { ...d, attachments: newAtts } : d
          ),
        };
      });
    } catch (e) {
      console.warn("Refresh diary attachments failed");
    }
  }

  async function refreshTrialAttachments() {
    try {
      const atts = await getAttachments("trial", trial.id);
      setTrial((prev) => ({ ...prev, attachments: atts }));
    } catch (e) {
      console.warn("Failed to refresh trial attachments");
    }
  }

  // Only render modal after trial is loaded
  return (
    <div className="w-full max-w-screen-xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow p-2 sm:p-4 md:p-8">
      {/* Top bar: Trial Details, Edit, Back */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
        <h1 className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
          Trial Details
        </h1>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 rounded bg-emerald-700 text-white"
            onClick={() => navigate(`/trials/${trial.id}/edit`, { state: { ...location.state, via: "view" } })}
          >
            Edit
          </button>
          {/* As Copy on the trial list: the wizard with a copy of this trial */}
          <button
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => navigate(`/trials/new`, { state: { trialId: trial.id } })}
          >
            Copy
          </button>
          {/* Closed or reopened only here, by a user, after a confirmation (never automatically) */}
          <button
            className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-800 text-white"
            onClick={() => setCloseDialog({ askFor: null })}
          >
            {isTrialClosed(trial) ? "Reopen" : "Close"}
          </button>
          {canDelete && (
            <button
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </button>
          )}
          <button
            className="px-4 py-2 rounded bg-emerald-600 text-white flex items-center gap-2"
            onClick={() => navigate(backTo)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 0 1-1.414-1.414L9.586 10 5.879 6.293a1 1 0 1 1 1.414-1.414l4.707 4.707a1 1 0 0 1 0 1.414l-4.707 4.707z"
                clipRule="evenodd"
              />
            </svg>
            Back
          </button>
        </div>
      </div>
      {/* The trial uses archived master data (it is not archived itself) */}
      <ArchivedDataBanner trial={trial} />
      {/* Responsive masonry-style grid for cards */}
      <div className="w-full mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 auto-rows-fr">
          {/* General Info */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow col-span-1 md:col-span-2 xl:col-span-2 flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">General Info</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><span className="font-semibold">Trial No:</span> {trial.trialno}</div>
              <div><span className="font-semibold">Year:</span> {trial.Year}</div>
              <div><span className="font-semibold">Soil Sample:</span> {trial.EarthSample}</div>
              <div><span className="font-semibold">Location Target:</span> {trial.locationtarget}</div>
              <div><span className="font-semibold">Local Reference:</span> {trial.LocalReference}</div>
              <div><span className="font-semibold">Trial Address:</span> {trial.TrialAddress}</div>
              <div><span className="font-semibold">{CO_LABEL}:</span> {trial.PO}<ArchivedBadge row={trial.project?.contract} /></div>
              <div><span className="font-semibold">Contract Amount:</span> {trial.Contract_Amount}</div>
              <div><span className="font-semibold">Field Phase Start:</span> {formatDate(trial.FieldFaseStart)}</div>
              <div><span className="font-semibold">Field Phase End:</span> {formatDate(trial.FieldFaseEnd)}</div>
            </div>
          </div>
          {/* Map Card - only show if coordinates are present and valid */}
          {position && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow flex flex-col items-center justify-center col-span-1">
              <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Trial Location</div>
              <div className="mb-2 text-xs text-center text-gray-500">
                <span className="block" data-testid="trial-gps">{formatGps(trial.PosGPSLatitude, trial.PosGPSLongitude)}</span>
              </div>
              <TrialMap lat={position.lat} lng={position.lng} />
            </div>
          )}
          {/* Customer */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Customer</div>
            <div className="mb-1"><span className="font-semibold">Name:</span> {trial.customer?.name || '–'}<ArchivedBadge row={trial.customer} /></div>
            <div className="mb-1"><span className="font-semibold">Customer No:</span> {trial.customer?.customerid ?? '–'}</div>
          </div>
          {/* Project */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Project</div>
            <div className="mb-1"><span className="font-semibold">Protocol No:</span> {trial.project?.protocolno || '–'}<ArchivedBadge row={trial.project} /></div>
            <div className="mb-1"><span className="font-semibold">{CO_LABEL}:</span> {trial.project?.contract?.co ?? '–'}<ArchivedBadge row={trial.project?.contract} /></div>
            <div className="mb-1"><span className="font-semibold">Contract Description:</span> {trial.project?.contract?.description || '–'}</div>
            <div className="mb-1"><span className="font-semibold">Contract Year:</span> {trial.project?.contract?.year || '–'}</div>
            <div className="mb-1"><span className="font-semibold">Customer Contact:</span> {trial.CustomerContact}</div>
          </div>
          {/* Study Director */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Study Director</div>
            <div className="flex flex-col items-center mb-2">
              <div className="mb-2 size-16 rounded-full bg-emerald-200 dark:bg-emerald-900 flex items-center justify-center overflow-hidden">
                <SecuredImage
                  src={
                    trial.study_director?.avatar
                      ? `avatars/${trial.study_director.avatar.replace(
                        /^avatars\//,
                        ""
                      )}`
                      : undefined
                  }
                  alt={trial.study_director?.name || "Study Director"}
                  className="w-full h-full rounded-full object-cover"
                  fallback={
                    <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                      {trial.study_director?.initials || "?"}
                    </span>
                  }
                />
              </div>
              <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">
                {trial.study_director?.name || trial.studydirector || "Unknown"}<ArchivedBadge row={trial.study_director} />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {trial.study_director?.email || "No email provided"}
              </div>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {trial.study_director?.initials && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
                    Initials: {trial.study_director.initials}
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Field Technician */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Field Technician</div>
            <div className="flex flex-col items-center mb-2">
              <div className="mb-2 size-16 rounded-full bg-emerald-200 dark:bg-emerald-900 flex items-center justify-center overflow-hidden">
                <SecuredImage
                  src={trial.field_technician?.avatar ? `avatars/${trial.field_technician.avatar.replace(/^avatars\//, '')}` : undefined}
                  alt={trial.field_technician?.name || 'Field Technician'}
                  className="w-full h-full rounded-full object-cover"
                  fallback={<span className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{trial.field_technician?.initials || '?'}</span>}
                />
              </div>
              <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{trial.field_technician?.name || 'Unknown'}<ArchivedBadge row={trial.field_technician} /></div>
              <div className="text-sm text-gray-600 dark:text-gray-300">{trial.field_technician?.email || 'No email provided'}</div>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {trial.field_technician?.initials && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">Initials: {trial.field_technician.initials}</span>
                )}
              </div>
            </div>
          </div>
          {/* Principal Investigator */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Principal Investigator</div>
            <div className="flex flex-col items-center mb-2">
              <div className="mb-2 size-16 rounded-full bg-emerald-200 dark:bg-emerald-900 flex items-center justify-center overflow-hidden">
                <SecuredImage
                  src={trial.principal_investigator?.avatar ? `avatars/${trial.principal_investigator.avatar.replace(/^avatars\//, '')}` : undefined}
                  alt={trial.principal_investigator?.name || 'Principal Investigator'}
                  className="w-full h-full rounded-full object-cover"
                  fallback={<span className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{trial.principal_investigator?.initials || '?'}</span>}
                />
              </div>
              <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{trial.principal_investigator?.name || 'Unknown'}<ArchivedBadge row={trial.principal_investigator} /></div>
              <div className="text-sm text-gray-600 dark:text-gray-300">{trial.principal_investigator?.email || 'No email provided'}</div>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {trial.principal_investigator?.initials && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">Initials: {trial.principal_investigator.initials}</span>
                )}
              </div>
            </div>
          </div>
          {/* Trial Host */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Trial Host</div>
            <div className="mb-1"><span className="font-semibold">Name:</span> {trial.host?.name}<ArchivedBadge row={trial.host} /></div>
            <div className="mb-1"><span className="font-semibold">Address:</span> {trial.host?.address}</div>
            <div className="mb-1"><span className="font-semibold">Phone:</span> {trial.host?.phone}</div>
            <div className="mb-1"><span className="font-semibold">Email:</span> {trial.host?.email}</div>
            <div className="mb-1"><span className="font-semibold">Country:</span> {trial.host?.country}</div>
          </div>
          {/* Trial Details */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow col-span-1 md:col-span-2 xl:col-span-4 flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Trial Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
              <div><span className="font-semibold">Country:</span> {trial.country?.name || '–'}<ArchivedBadge row={trial.country} /></div>
              <div><span className="font-semibold">Crop:</span> {trial.crop?.name}<ArchivedBadge row={trial.crop} /> <span className="text-xs text-gray-500">{trial.crop?.description}</span></div>
              <div><span className="font-semibold">Study Type:</span> {trial.study_type?.name}<ArchivedBadge row={trial.study_type} /></div>
              <div><span className="font-semibold">Trial Type:</span> {trial.trial_type?.name}<ArchivedBadge row={trial.trial_type} /></div>
              <div><span className="font-semibold">Treatment Count:</span> {trial.TreatmentCount}</div>
              <div><span className="font-semibold">Replication Count:</span> {trial.ReplicationCount}</div>
              <div><span className="font-semibold">Application Count:</span> {trial.ApplicationCount}</div>
              <div><span className="font-semibold">Visit Count:</span> {trial.VisitCount}</div>
              <div><span className="font-semibold">Plot Size L:</span> {trial.PlotSizeL}</div>
              <div><span className="font-semibold">Plot Size W:</span> {trial.PlotSizeB}</div>
              {/* Only while the activity is Yes: a number left over from before is not shown (utils/samples.js) */}
              {SAMPLE_FIELDS.filter(({ flag }) => takesSamples(trial, flag)).map(({ field, label }) => (
                <div key={field} data-testid={`trial-details-${field}`}><span className="font-semibold">{label}:</span> {shownSamples(trial, field)}</div>
              ))}
              {hasSamples(trial) && (
                <div data-testid="trial-details-samples">
                  <SamplesDialog trial={trial} onSaved={saveSamples} size="sm" className="h-7" />
                </div>
              )}
              {/* FT and PI removed from this grid - they are shown in their own cards above */}
            </div>
            <div className="mt-4">
              <div className="font-semibold text-md mb-2">Trial Status</div>
              <div className="mb-3 text-sm" data-testid="trial-open-closed">
                <span className="font-semibold">Status:</span>{' '}
                <TrialStatusBadge closed={trial.Closed} />
              </div>
              {/* The activities under the old system's headings, each with its value in words */}
              {ACTIVITY_CATEGORIES.map((category) => ({
                title: category.title,
                statuses: vanoCodes
                  .filter((entry) => entry.visible && entry.category === category.key)
                  .map((entry) => ({ label: entry.label, value: trial[entry.field] })),
              })).filter((group) => group.statuses.length > 0).map((group) => (
              <div key={group.title} className="mb-2">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{group.title}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 text-sm">
                {group.statuses.map((status) => {
                  const shown = activityValue(status.value);
                  return (
                    <div
                      key={status.label}
                      data-value={shown.word}
                      className={`px-2 py-1 rounded font-semibold flex items-center justify-between gap-2 ${shown.className}`}
                    >
                      <span>{status.label}</span>
                      <span className="text-xs font-normal">{shown.word}</span>
                    </div>
                  );
                })}
              </div>
              </div>
              ))}
            </div>
          </div>
          {/* Notes */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow col-span-1 md:col-span-2 xl:col-span-4 flex flex-col">
            <div className="font-semibold text-lg mb-2 text-emerald-700 dark:text-emerald-400">Notes</div>
            <div className="flex flex-col gap-3">
              {/* General Notes */}
              <div
                className="text-sm p-3 rounded bg-gray-100 dark:bg-gray-800 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900 transition"
                onClick={() => setNoteModal({ open: true, field: 'Noter1', value: trial.Noter1 || '' })}
                title="Click to edit General Notes"
              >
                <span className="font-semibold block mb-1">General Notes:</span>
                <p className={`whitespace-pre-wrap ${trial.Noter1 ? '' : 'italic text-gray-400'}`}>{trial.Noter1 || 'No general notes available.'}</p>
              </div>
              {/* Visits/Applications Notes */}
              <div
                className="text-sm p-3 rounded bg-gray-100 dark:bg-gray-800 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900 transition"
                onClick={() => setNoteModal({ open: true, field: 'Noter2', value: trial.Noter2 || '' })}
                title="Click to edit Visits/Applications Notes"
              >
                <span className="font-semibold block mb-1">Visits/Applications:</span>
                <p className={`whitespace-pre-wrap ${trial.Noter2 ? '' : 'italic text-gray-400'}`}>{trial.Noter2 || 'No visits/applications notes available.'}</p>
              </div>
              {/* Maintenance Notes */}
              <div
                className="text-sm p-3 rounded bg-gray-100 dark:bg-gray-800 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900 transition"
                onClick={() => setNoteModal({ open: true, field: 'Noter3', value: trial.Noter3 || '' })}
                title="Click to edit Maintenance Notes"
              >
                <span className="font-semibold block mb-1">Maintenance:</span>
                <p className={`whitespace-pre-wrap ${trial.Noter3 ? '' : 'italic text-gray-400'}`}>{trial.Noter3 || 'No maintenance notes available.'}</p>
              </div>
            </div>
            {/* Note Edit Modal */}
            <Dialog open={noteModal.open} onClose={() => setNoteModal({ open: false, field: '', value: '' })} className="fixed z-50 inset-0 overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
                <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-lg w-full mx-auto p-6 z-10">
                  <div className="flex justify-between items-center mb-4">
                    <div className="font-semibold text-lg text-emerald-700 dark:text-emerald-300">Edit Note</div>
                    <button
                      className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl"
                      onClick={() => setNoteModal({ open: false, field: '', value: '' })}
                      aria-label="Close"
                    >
                      &times;
                    </button>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Note</label>
                    <textarea
                      className="w-full rounded border px-3 py-2 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-400 focus:border-emerald-500 min-h-[120px]"
                      value={noteModal.value}
                      onChange={e => setNoteModal(modal => ({ ...modal, value: e.target.value }))}
                      disabled={noteSaving}
                      autoFocus
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={() => setNoteModal({ open: false, field: '', value: '' })}
                      disabled={noteSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors"
                      onClick={handleNoteSave}
                      disabled={noteSaving}
                    >
                      {noteSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </Dialog>
          </div>
        </div>
      </div>
      {/* Always show the attachments card, with empty state if none */}
      <div className="mb-8 w-full">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-lg text-emerald-700 dark:text-emerald-400">
              Trial Video/Photo
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                onClick={() => setShowCameraModal(true)}
              >
                Add Photo/Video
              </button>
              <button
                type="button"
                className="px-3 py-1 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                onClick={() => setShowFileUploaderModal(true)}
              >
                Upload Files
              </button>
            </div>
          </div>
          {/* Camera Modal */}
          {showCameraModal && (
            <Dialog
              open={showCameraModal}
              onClose={() => setShowCameraModal(false)}
              className="fixed z-50 inset-0 overflow-y-auto"
            >
              <div className="flex items-center justify-center min-h-screen px-4">
                <div
                  className="fixed inset-0 bg-black bg-opacity-50"
                  aria-hidden="true"
                />
                <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto p-6 z-10">
                  <div className="flex justify-between items-center mb-4">
                    <div className="font-semibold text-lg text-emerald-700 dark:text-emerald-400">
                      Add Photo/Video
                    </div>
                    <button
                      className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl"
                      onClick={() => setShowCameraModal(false)}
                      aria-label="Close"
                    >
                      &times;
                    </button>
                  </div>
                  <Camera
                    model_type="trial"
                    model_id={trial.id}
                    autoActivate={true}
                    onPhotoCapture={async () => {
                      await refreshTrialAttachments();
                      setShowCameraModal(false);
                    }}
                    onVideoCapture={async () => {
                      await refreshTrialAttachments();
                      setShowCameraModal(false);
                    }}
                  />
                </div>
              </div>
            </Dialog>
          )}

          {/* File Uploader Modal */}
          {showFileUploaderModal && (
            <Dialog
              open={showFileUploaderModal}
              onClose={() => setShowFileUploaderModal(false)}
              className="fixed z-50 inset-0 overflow-y-auto"
            >
              <div className="flex items-center justify-center min-h-screen px-4">
                <div
                  className="fixed inset-0 bg-black bg-opacity-50"
                  aria-hidden="true"
                />
                <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto p-6 z-10">
                  <div className="flex justify-between items-center mb-4">
                    <div className="font-semibold text-lg text-blue-700 dark:text-blue-400">
                      Upload Files
                    </div>
                    <button
                      className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl"
                      onClick={() => setShowFileUploaderModal(false)}
                      aria-label="Close"
                    >
                      &times;
                    </button>
                  </div>
                  <FileUploader
                    model_type="trial"
                    model_id={trial.id}
                    onFileUpload={async () => {
                      await refreshTrialAttachments();
                      setShowFileUploaderModal(false);
                    }}
                    acceptedFileTypes="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
                    maxFileSize={50 * 1024 * 1024}
                    multiple={true}
                    title=""
                  />
                </div>
              </div>
            </Dialog>
          )}

          {Array.isArray(trial.attachments) && trial.attachments.length > 0 ? (
            <div className="flex flex-wrap gap-4">
              {trial.attachments.map((att) => {
                const isImage =
                  att.mime_type && att.mime_type.startsWith("image/");
                const isVideo =
                  att.mime_type && att.mime_type.startsWith("video/");
                let url = "";
                if (att.path) {
                  if (att.path.startsWith("http")) {
                    url = att.path;
                  } else {
                    // Always ensure single slash between attachments and rest
                    url = `field/${att.path.replace(
                      /^\/?(attachments\/)?/,
                      ""
                    )}`;
                  }
                }
                return (
                  <div key={att.id} className="flex flex-col items-center w-32">
                    <button
                      type="button"
                      className="focus:outline-none"
                      onClick={() => {
                        setAttachmentModal({ open: true, attachment: att });
                        setEditDescription(att.description || "");
                        setEditMode(false);
                      }}
                    >
                      {isImage && url ? (
                        <SecuredImage
                          src={url}
                          alt={att.original_filename || att.filename}
                          className="w-28 h-20 object-cover rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1"
                          fallback={
                            <div className="w-28 h-20 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1 text-xs text-gray-600 dark:text-gray-300">
                              Image unavailable
                            </div>
                          }
                        />
                      ) : isVideo && url ? (
                        <video
                          src={url}
                          className="w-28 h-20 object-cover rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1"
                          controls
                        />
                      ) : (
                        <div className="w-28 h-20 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1">
                          <span className="text-xs text-gray-600 dark:text-gray-300">
                            {att.mime_type || "File"}
                          </span>
                        </div>
                      )}
                    </button>
                    <div
                      className="text-xs text-gray-600 dark:text-gray-300 truncate w-full text-center"
                      title={att.original_filename || att.filename}
                    >
                      {att.original_filename || att.filename}
                    </div>
                    {isImage && <AttachmentDownloadButton attachment={att} className="mt-1" />}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-200 px-6 py-10 md:py-14 dark:border-gray-700 flex flex-col items-center justify-center bg-white dark:bg-gray-900">
              <div className="flex flex-col gap-3 text-center max-w-sm">
                <div className="inline-flex items-center justify-center mx-auto rounded-full bg-emerald-50 dark:bg-emerald-950 p-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="hi-outline hi-photo inline-block size-8 text-emerald-500 dark:text-emerald-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 6.75A2.25 2.25 0 0 1 5.25 4.5h13.5A2.25 2.25 0 0 1 21 6.75v10.5A2.25 2.25 0 0 1 18.75 19.5H5.25A2.25 2.25 0 0 1 3 17.25V6.75Zm0 0 5.25 5.25a3 3 0 0 0 4.243 0l.457-.457a3 3 0 0 1 4.243 0L21 15.75M7.5 8.25h.008v.008H7.5V8.25Z"
                    />
                  </svg>
                </div>
                <h3 className="mb-1 text-xl font-bold text-emerald-700 dark:text-emerald-400">
                  No Attachments
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-snug">
                  Add photos or videos during field work to document progress.
                </p>
                <div>
                  <button
                    type="button"
                    onClick={() => setShowCameraModal(true)}
                    className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4"
                    >
                      <path d="M6.5 3A1.5 1.5 0 0 0 5 4.5v11A1.5 1.5 0 0 0 6.5 17h7a1.5 1.5 0 0 0 1.5-1.5v-11A1.5 1.5 0 0 0 13.5 3h-7ZM10 5a.75.75 0 0 1 .75.75v2.5h2.5a.75.75 0 0 1 0 1.5h-2.5v2.5a.75.75 0 0 1-1.5 0v-2.5h-2.5a.75.75 0 0 1 0-1.5h2.5v-2.5A.75.75 0 0 1 10 5Z" />
                    </svg>
                    Add Attachment
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Attachment Modal */}
      <Dialog
        open={attachmentModal.open}
        onClose={() => setAttachmentModal({ open: false, attachment: null })}
        className="fixed z-50 inset-0 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen px-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto p-6 z-10">
            {attachmentModal.attachment && (
              <>
                <div className="mb-4 flex flex-col items-center">
                  {(() => {
                    const att = attachmentModal.attachment;
                    const isImage =
                      att.mime_type && att.mime_type.startsWith("image/");
                    const isVideo =
                      att.mime_type && att.mime_type.startsWith("video/");
                    let url = "";
                    if (att.path) {
                      if (att.path.startsWith("http")) {
                        url = att.path;
                      } else {
                        url = `field/${att.path.replace(
                          /^\/?(attachments\/)?/,
                          ""
                        )}`;
                      }
                    }
                    if (isImage && url) {
                      return (
                        <SecuredImage
                          src={url}
                          alt={att.original_filename || att.filename}
                          className="max-h-80 w-auto object-contain rounded border border-gray-300 dark:border-gray-700 shadow mb-2"
                          fallback={
                            <div className="w-64 h-40 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-700 shadow text-xs text-gray-600 dark:text-gray-300">
                              Image unavailable
                            </div>
                          }
                        />
                      );
                    } else if (isVideo && url) {
                      return (
                        <video
                          src={url}
                          className="max-h-80 w-auto object-contain rounded border border-gray-300 dark:border-gray-700 shadow mb-2"
                          controls
                          autoPlay
                        />
                      );
                    } else {
                      return (
                        <div className="w-64 h-40 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-700 shadow text-xs text-gray-600 dark:text-gray-300">
                          {att.mime_type || "File"}
                        </div>
                      );
                    }
                  })()}
                  <div
                    className="text-xs text-gray-600 dark:text-gray-300 truncate w-full text-center"
                    title={
                      attachmentModal.attachment.original_filename ||
                      attachmentModal.attachment.filename
                    }
                  >
                    {attachmentModal.attachment.original_filename ||
                      attachmentModal.attachment.filename}
                  </div>
                  {attachmentModal.attachment.mime_type?.startsWith("image/") && (
                    <AttachmentDownloadButton attachment={attachmentModal.attachment} className="mt-2" />
                  )}
                </div>
                {/* Description and actions */}
                <div className="mb-2">
                  {editMode ? (
                    <div className="flex flex-col gap-2">
                      <textarea
                        className="border rounded px-2 py-1 w-full text-sm font-mono min-h-[80px] resize-y"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        disabled={attachmentActionLoading}
                        autoFocus
                        placeholder="Enter description in Markdown..."
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          className="px-2 py-1 bg-emerald-600 text-white rounded text-xs"
                          disabled={attachmentActionLoading}
                          onClick={async () => {
                            setAttachmentActionLoading(true);
                            try {
                              await axios.patch(
                                `${config.apiBaseUrl}/${attachmentModal.attachment.id}`,
                                { description: editDescription },
                                {
                                  headers: {
                                    Authorization: token
                                      ? `Bearer ${token}`
                                      : undefined,
                                  },
                                }
                              );
                              // Update local state
                              setTrial((prev) => ({
                                ...prev,
                                attachments: prev.attachments.map((a) =>
                                  a.id === attachmentModal.attachment.id
                                    ? { ...a, description: editDescription }
                                    : a
                                ),
                              }));
                              setEditMode(false);
                            } catch (e) {
                              alert("Failed to update description.");
                            } finally {
                              setAttachmentActionLoading(false);
                            }
                          }}
                        >
                          Save
                        </button>
                        <button
                          className="px-2 py-1 bg-gray-300 text-gray-800 rounded text-xs"
                          disabled={attachmentActionLoading}
                          onClick={() => {
                            setEditMode(false);
                            setEditDescription(
                              attachmentModal.attachment.description || ""
                            );
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
                        {attachmentModal.attachment.description ? (
                          <ReactMarkdown>
                            {attachmentModal.attachment.description}
                          </ReactMarkdown>
                        ) : (
                          <span className="italic text-gray-400">
                            No description
                          </span>
                        )}
                      </div>
                      {canEditAttachment && (
                        <div>
                          <button
                            className="mt-1 px-2 py-1 bg-emerald-500 text-white rounded text-xs"
                            onClick={() => setEditMode(true)}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Delete button */}
                {canEditAttachment && (
                  <div className="flex justify-end">
                    <button
                      className="px-3 py-1 bg-red-600 text-white rounded text-xs"
                      disabled={attachmentActionLoading}
                      onClick={() => setAttachmentDeleteOpen(true)}
                    >
                      Delete
                    </button>
                  </div>
                )}
                {/* Inside the modal: it makes everything outside it inert */}
                <ConfirmDeleteDialog
                  open={attachmentDeleteOpen}
                  title="Delete file"
                  name={attachmentModal.attachment.original_filename || attachmentModal.attachment.filename}
                  nameLabel="file name"
                  onDelete={() => axios.delete(
                    // Route::delete('/attachments/{attachment}', [AttachmentController::class, 'destroy']);
                    `${config.apiBaseUrl}/attachments/${attachmentModal.attachment.id}`,
                    { headers: { Authorization: token ? `Bearer ${token}` : undefined } }
                  )}
                  onClose={() => setAttachmentDeleteOpen(false)}
                  onDone={() => {
                    // Remove from local state
                    setTrial((prev) => ({
                      ...prev,
                      attachments: prev.attachments.filter(
                        (a) => a.id !== attachmentModal.attachment.id
                      ),
                    }));
                    setAttachmentDeleteOpen(false);
                    setAttachmentModal({ open: false, attachment: null });
                  }}
                />
                <button
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl"
                  onClick={() =>
                    setAttachmentModal({ open: false, attachment: null })
                  }
                  aria-label="Close"
                >
                  &times;
                </button>
              </>
            )}
          </div>
        </div>
      </Dialog>
      {/* Diary Section */}
      <div className="mb-8 w-full">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
              Diary Entries
            </h2>
            <TaskStatusLegend className="mt-2" />
          </div>
          <button
            type="button"
            className="px-4 py-2 rounded bg-emerald-600 text-white text-md font-semibold hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            onClick={() => setDiaryModalOpen(true)}
          >
            Add Diary Entry
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Visits */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow">
            <div className="mb-2 font-semibold text-lg text-emerald-700 dark:text-emerald-400">
              Visits
            </div>
            {visitsDiaries.length === 0 ? (
              <div className="text-gray-500">
                No visits diary entries found.
              </div>
            ) : (
              <ul className="flex flex-col gap-6">
                {visitsDiaries.map((entry, idx) => {
                  const attCount = Array.isArray(entry.attachments)
                    ? entry.attachments.length
                    : 0;
                  const dateStr = formatDisplayDate(entry.donedate);
                  const taskBadge = getTaskBadge(enrichTask(entry, diaries));
                  const cancelled = entry.cancelled == true;
                  const cancelledClasses = cancelled
                    ? "opacity-50 text-gray-400 dark:text-gray-600"
                    : "";
                  return (
                    <li
                      key={entry.id || idx}
                      className={`relative flex rounded-md border-l-4 pl-3 ${taskBadge.borderColor} ${taskBadge.surfaceColor}`}
                    >
                      <div className="flex flex-col w-full">
                        <div className="flex items-center justify-between gap-2">
                          <div className={`flex min-w-0 flex-wrap items-center gap-2 ${cancelledClasses}`}>
                            <h4
                              className={`text-sm font-bold`}
                            >
                              {entry.task || "Diary Entry"}
                            </h4>
                            <TaskStatusPill status={taskBadge.status} text={taskBadge.text} title={taskBadge.detail} />

                          </div>
                          <div className="flex shrink-0 items-stretch gap-2">
                            <button
                              type="button"
                              onClick={
                                cancelled
                                  ? undefined
                                  : () => openDiaryAttachments(entry)
                              }
                              disabled={cancelled}
                              className={`inline-flex items-center gap-1 rounded px-2 text-[11px] font-semibold tracking-wide border shadow-sm transition h-7 ${attCount > 0
                                ? "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 border-emerald-300/70 dark:border-emerald-700"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600"
                                } ${cancelled ? "cursor-not-allowed" : ""}`}
                              title={
                                attCount > 0
                                  ? `${attCount} attachment${attCount === 1 ? "" : "s"
                                  }`
                                  : "No attachments yet"
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="w-3 h-3"
                              >
                                <path d="M15.145 5.143a3.5 3.5 0 0 0-4.95 0L3.439 11.9a2.5 2.5 0 1 0 3.536 3.535l5.657-5.656a1.5 1.5 0 0 0-2.122-2.122L7.05 10.117a.75.75 0 0 1-1.06-1.06l3.46-3.46a3 3 0 1 1 4.243 4.243l-5.656 5.657a4 4 0 1 1-5.657-5.657l6.757-6.757a5 5 0 0 1 7.07 7.071l-6.01 6.01a.75.75 0 0 1-1.06-1.061l6.01-6.01a3.5 3.5 0 0 0 0-4.95Z" />
                              </svg>
                              {attCount > 0 ? attCount : "0"}
                            </button>
                            {!cancelled && !entry.donedate && (
                              <CancelTaskDialog
                                trialId={trial.id}
                                task={entry}
                                onTaskCancel={fetchTrial}
                                size="sm"
                                className="h-7"
                              />
                            )}
                            <button
                              className={`px-3 h-7 flex items-center bg-emerald-500 text-white rounded text-xs disabled:opacity-50`}
                              onClick={() => handleEditDiary(entry)}
                              disabled={loadingDiaryEdit}
                            >
                              {loadingDiaryEdit ? "…" : "Edit"}
                            </button>
                            {canDelete && (
                              <button
                                className="px-3 h-7 flex items-center bg-red-100 text-red-800 hover:bg-red-200 rounded text-xs"
                                aria-label="Delete task"
                                onClick={() => setEntryToDelete(entry)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                        <div className={`mt-0.5 text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300 flex flex-wrap gap-x-3 gap-y-0.5 ${cancelledClasses} `}>
                          {entry.description && (<span className="w-full text-xs text-zinc-700 dark:text-zinc-200">{entry.description}</span>)}
                          {dateStr && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Done
                              </span>
                              {dateStr}
                            </span>
                          )}
                          {/* Always both: the customer's Time interval (locked), then Agrolab's Planned date; BBCH on its own line */}
                          <div className="w-full">
                            <TaskDateLines task={entry} size="row" bbch />
                          </div>
                          {entry.persons && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Persons
                              </span>
                              {entry.persons}
                            </span>
                          )}
                          {cancelled && entry.cancelled_reason && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Cancelled
                              </span>
                              {entry.cancelled_reason}
                              {entry.cancelled_at && ` (${formatDisplayDate(entry.cancelled_at)})`}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {/* Applications */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow">
            <div className="mb-2 font-semibold text-lg text-emerald-700 dark:text-emerald-400">
              Applications
            </div>
            {applicationsDiaries.length === 0 ? (
              <div className="text-gray-500">
                No applications diary entries found.
              </div>
            ) : (
              <ul className="flex flex-col gap-6">
                {applicationsDiaries.map((entry, idx) => {
                  const attCount = Array.isArray(entry.attachments)
                    ? entry.attachments.length
                    : 0;
                  const dateStr = formatDisplayDate(entry.donedate);
                  const taskBadge = getTaskBadge(enrichTask(entry, diaries));
                  const cancelled = entry.cancelled == true;
                  const cancelledClasses = cancelled
                    ? "opacity-50 text-gray-400 dark:text-gray-600"
                    : "";
                  return (
                    <li
                      key={entry.id || idx}
                      className={`relative flex rounded-md border-l-4 pl-3 ${taskBadge.borderColor} ${taskBadge.surfaceColor}`}
                    >
                      <div className="flex flex-col w-full">
                        <div className="flex items-center justify-between gap-2">
                          <div className={`flex min-w-0 flex-wrap items-center gap-2 ${cancelledClasses}`}>
                            <h4
                              className={`text-sm font-bold`}
                            >
                              {entry.task || "Diary Entry"}
                            </h4>
                            <TaskStatusPill status={taskBadge.status} text={taskBadge.text} title={taskBadge.detail} />

                          </div>
                          <div className="flex shrink-0 items-stretch gap-2">
                            <button
                              type="button"
                              onClick={
                                cancelled
                                  ? undefined
                                  : () => openDiaryAttachments(entry)
                              }
                              disabled={cancelled}
                              className={`inline-flex items-center gap-1 rounded px-2 text-[11px] font-semibold tracking-wide border shadow-sm transition h-7 ${attCount > 0
                                ? "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 border-emerald-300/70 dark:border-emerald-700"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600"
                                } ${cancelled ? "cursor-not-allowed" : ""}`}
                              title={
                                attCount > 0
                                  ? `${attCount} attachment${attCount === 1 ? "" : "s"
                                  }`
                                  : "No attachments yet"
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="w-3 h-3"
                              >
                                <path d="M15.145 5.143a3.5 3.5 0 0 0-4.95 0L3.439 11.9a2.5 2.5 0 1 0 3.536 3.535l5.657-5.656a1.5 1.5 0 0 0-2.122-2.122L7.05 10.117a.75.75 0 0 1-1.06-1.06l3.46-3.46a3 3 0 1 1 4.243 4.243l-5.656 5.657a4 4 0 1 1-5.657-5.657l6.757-6.757a5 5 0 0 1 7.07 7.071l-6.01 6.01a.75.75 0 0 1-1.06-1.061l6.01-6.01a3.5 3.5 0 0 0 0-4.95Z" />
                              </svg>
                              {attCount > 0 ? attCount : "No files"}
                            </button>
                            {!cancelled && !entry.donedate && (
                              <CancelTaskDialog
                                trialId={trial.id}
                                task={entry}
                                onTaskCancel={fetchTrial}
                                size="sm"
                                className="h-7"
                              />
                            )}
                            <button
                              className="px-3 h-7 flex items-center bg-emerald-500 text-white rounded text-xs disabled:opacity-50"
                              onClick={() => handleEditDiary(entry)}
                              disabled={loadingDiaryEdit}
                            >
                              {loadingDiaryEdit ? "…" : "Edit"}
                            </button>
                            {canDelete && (
                              <button
                                className="px-3 h-7 flex items-center bg-red-100 text-red-800 hover:bg-red-200 rounded text-xs"
                                aria-label="Delete task"
                                onClick={() => setEntryToDelete(entry)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                        <div className={`mt-0.5 text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300 flex flex-wrap gap-x-3 gap-y-0.5 ${cancelledClasses}`}>
                          {entry.description && (<span className="w-full text-xs text-zinc-700 dark:text-zinc-200">{entry.description}</span>)}
                          {dateStr && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Done
                              </span>
                              {dateStr}
                            </span>
                          )}
                          {/* Always both: the customer's Time interval (locked), then Agrolab's Planned date; BBCH on its own line */}
                          <div className="w-full">
                            <TaskDateLines task={entry} size="row" bbch />
                          </div>
                          {entry.vano && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                VA
                              </span>
                              {entry.vano}
                            </span>
                          )}
                          {entry.persons && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Persons
                              </span>
                              {entry.persons}
                            </span>
                          )}
                          {cancelled && entry.cancelled_reason && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Cancelled
                              </span>
                              {entry.cancelled_reason}
                              {entry.cancelled_at && ` (${formatDisplayDate(entry.cancelled_at)})`}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {/* Activities */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 shadow">
            <div className="mb-2 font-semibold text-lg text-emerald-700 dark:text-emerald-400">
              Activities
            </div>
            {activitiesDiaries.length === 0 ? (
              <div className="text-gray-500">
                No activities diary entries found.
              </div>
            ) : (
              <ul className="flex flex-col gap-6">
                {activityRows.map((entry, idx) => {
                  if (entry.heading) {
                    return (
                      <li key={entry.heading} className="-mb-4 text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                        <h3>{entry.heading}</h3>
                      </li>
                    );
                  }
                  const attCount = Array.isArray(entry.attachments)
                    ? entry.attachments.length
                    : 0;
                  const dateStr = formatDisplayDate(entry.donedate);
                  const taskBadge = getTaskBadge(enrichTask(entry, diaries));
                  const cancelled = entry.cancelled == true;
                  const cancelledClasses = cancelled
                    ? "opacity-50 text-gray-400 dark:text-gray-600"
                    : "";
                  return (
                    <li
                      key={entry.id || idx}
                      className={`relative flex rounded-md border-l-4 pl-3 ${taskBadge.borderColor} ${taskBadge.surfaceColor} ${cancelledClasses}`}
                    >
                      <div className="flex flex-col w-full">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <h4
                              className={`text-sm font-bold ${cancelled ? "line-through" : ""
                                }`}
                            >
                              {entry.task || "Diary Entry"}
                            </h4>
                            <TaskStatusPill status={taskBadge.status} text={taskBadge.text} title={taskBadge.detail} />

                          </div>
                          <div className="flex shrink-0 items-stretch gap-2">
                            {!cancelled && SAMPLE_TASKS.has(entry.vano) && (
                              <SamplesDialog trial={trial} onSaved={saveSamples} size="sm" className="h-7" />
                            )}
                            <button
                              type="button"
                              onClick={
                                cancelled
                                  ? undefined
                                  : () => openDiaryAttachments(entry)
                              }
                              disabled={cancelled}
                              className={`inline-flex items-center gap-1 rounded px-2 text-[11px] font-semibold tracking-wide border shadow-sm transition h-7 ${attCount > 0
                                ? "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 border-emerald-300/70 dark:border-emerald-700"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600"
                                } ${cancelled ? "cursor-not-allowed" : ""}`}
                              title={
                                attCount > 0
                                  ? `${attCount} attachment${attCount === 1 ? "" : "s"
                                  }`
                                  : "No attachments yet"
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="w-3 h-3"
                              >
                                <path d="M15.145 5.143a3.5 3.5 0 0 0-4.95 0L3.439 11.9a2.5 2.5 0 1 0 3.536 3.535l5.657-5.656a1.5 1.5 0 0 0-2.122-2.122L7.05 10.117a.75.75 0 0 1-1.06-1.06l3.46-3.46a3 3 0 1 1 4.243 4.243l-5.656 5.657a4 4 0 1 1-5.657-5.657l6.757-6.757a5 5 0 0 1 7.07 7.071l-6.01 6.01a.75.75 0 0 1-1.06-1.061l6.01-6.01a3.5 3.5 0 0 0 0-4.95Z" />
                              </svg>
                              {attCount > 0 ? attCount : "No files"}
                            </button>
                            {!cancelled && !entry.donedate && (
                              <CancelTaskDialog
                                trialId={trial.id}
                                task={entry}
                                onTaskCancel={fetchTrial}
                                size="sm"
                                className="h-7"
                              />
                            )}
                            <button
                              className="px-3 h-7 flex items-center bg-emerald-500 text-white rounded text-xs disabled:opacity-50"
                              onClick={() => handleEditDiary(entry)}
                              disabled={loadingDiaryEdit}
                            >
                              {loadingDiaryEdit ? "…" : "Edit"}
                            </button>
                            {canDelete && (
                              <button
                                className="px-3 h-7 flex items-center bg-red-100 text-red-800 hover:bg-red-200 rounded text-xs"
                                aria-label="Delete task"
                                onClick={() => setEntryToDelete(entry)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mt-0.5 text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300 flex flex-wrap gap-x-3 gap-y-0.5">
                          {entry.description && (<span className="w-full text-xs text-zinc-700 dark:text-zinc-200">{entry.description}</span>)}
                          {dateStr && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Done
                              </span>
                              {dateStr}
                            </span>
                          )}
                          {/* Always both: the customer's Time interval (locked), then Agrolab's Planned date; BBCH on its own line */}
                          <div className="w-full">
                            <TaskDateLines task={entry} size="row" bbch />
                          </div>
                          {entry.vano && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                VA
                              </span>
                              {entry.vano}
                            </span>
                          )}
                          {entry.persons && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Persons
                              </span>
                              {entry.persons}
                            </span>
                          )}
                          {cancelled && entry.cancelled_reason && (
                            <span>
                              <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 text-zinc-500">
                                Cancelled
                              </span>
                              {entry.cancelled_reason}
                              {entry.cancelled_at && ` (${formatDisplayDate(entry.cancelled_at)})`}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
      {/* Diary Entry Modal (Add) */}
      <DiaryEntryModal
        open={!!diaryModalOpen}
        onClose={() => {
          setDiaryModalOpen(false);
          setEditingDiary(null);
        }}
        onSave={handleDiarySave}
        initialData={{ trial_id: trial?.id }}
        trialDiaries={diaries}
        onAttachmentUpdate={handleDiaryAttachmentUpdate}
      />
      {/* Diary Entry Modal (Edit) */}
      <DiaryEntryModal
        open={editDiaryModalOpen}
        onClose={() => {
          setEditDiaryModalOpen(false);
          setEditingDiary(null);
        }}
        onSave={handleEditDiarySave}
        initialData={
          editingDiary
            ? { ...editingDiary, trial_id: trial?.id }
            : { trial_id: trial?.id }
        }
        onAttachmentUpdate={handleDiaryAttachmentUpdate}
      />
      <CloseTrialDialog
        trial={{ ...trial, diaries }}
        open={!!closeDialog}
        askFor={closeDialog?.askFor}
        onOpenChange={(next) => { if (!next) setCloseDialog(null); }}
        onChanged={fetchTrial}
      />
      <Dialog
        open={diaryAttachmentsModal.open}
        onClose={() =>
          setDiaryAttachmentsModal({
            open: false,
            attachments: [],
            diary: null,
          })
        }
        className="fixed z-50 inset-0 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen px-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full mx-auto p-6 z-10">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-semibold text-lg text-emerald-700 dark:text-emerald-300">
                  Diary Attachments
                </div>
                {diaryAttachmentsModal.diary && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Task:{" "}
                    <span className="font-medium">
                      {diaryAttachmentsModal.diary.task || "Untitled"}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {diaryAttachmentsModal.diary && (
                  <>
                    <button
                      className="px-3 py-1 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                      onClick={() => setShowDiaryCameraModal(true)}
                    >
                      Add Photo/Video
                    </button>
                    <button
                      className="px-3 py-1 rounded bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                      onClick={() => setShowDiaryFileUploaderModal(true)}
                    >
                      Upload Files
                    </button>
                  </>
                )}
                <button
                  className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl leading-none"
                  onClick={() =>
                    setDiaryAttachmentsModal({
                      open: false,
                      attachments: [],
                      diary: null,
                    })
                  }
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="mb-4">
              {diaryAttachmentsModal.attachments.length === 0 ? (
                <div className="text-center py-10 text-gray-500 dark:text-gray-400 text-sm">
                  No attachments yet.
                </div>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {diaryAttachmentsModal.attachments.map((att) => {
                    const isImage =
                      att.mime_type && att.mime_type.startsWith("image/");
                    const isVideo =
                      att.mime_type && att.mime_type.startsWith("video/");
                    let url = "";
                    if (att.path) {
                      if (att.path.startsWith("http")) {
                        url = att.path;
                      } else {
                        url = `field/${att.path.replace(
                          /^\/?(attachments\/)?/,
                          ""
                        )}`;
                      }
                    }
                    return (
                      <div
                        key={att.id}
                        className="flex flex-col items-center w-32"
                      >
                        <div className="focus:outline-none">
                          {isImage && url ? (
                            <SecuredImage
                              src={url}
                              alt={att.original_filename || att.filename}
                              className="w-28 h-20 object-cover rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1"
                            />
                          ) : isVideo && url ? (
                            <video
                              src={url}
                              className="w-28 h-20 object-cover rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1"
                              controls={false}
                              muted
                            />
                          ) : (
                            <div className="w-28 h-20 bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-700 shadow-sm mb-1 flex items-center justify-center">
                              <span className="text-xs text-gray-500">
                                File
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-600 dark:text-gray-400 text-center truncate w-full">
                          {att.original_filename || att.filename}
                        </span>
                        {isImage && <AttachmentDownloadButton attachment={att} className="mt-1" />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>
      {showDiaryCameraModal && diaryAttachmentsModal.diary && (
        <Dialog
          open={showDiaryCameraModal}
          onClose={() => setShowDiaryCameraModal(false)}
          className="fixed z-50 inset-0 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              aria-hidden="true"
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto p-6 z-10">
              <div className="flex justify-between items-center mb-4">
                <div className="font-semibold text-lg text-emerald-700 dark:text-emerald-400">
                  Add Photo/Video
                </div>
                <button
                  className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl"
                  onClick={() => setShowDiaryCameraModal(false)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <Camera
                model_type="diary"
                model_id={diaryAttachmentsModal.diary.id}
                autoActivate={true}
                onPhotoCapture={async () => {
                  await refreshDiaryAttachments(diaryAttachmentsModal.diary);
                  setShowDiaryCameraModal(false);
                }}
                onVideoCapture={async () => {
                  await refreshDiaryAttachments(diaryAttachmentsModal.diary);
                  setShowDiaryCameraModal(false);
                }}
              />
            </div>
          </div>
        </Dialog>
      )}

      {/* Diary File Uploader Modal */}
      {showDiaryFileUploaderModal && diaryAttachmentsModal.diary && (
        <Dialog
          open={showDiaryFileUploaderModal}
          onClose={() => setShowDiaryFileUploaderModal(false)}
          className="fixed z-50 inset-0 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              aria-hidden="true"
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-auto p-6 z-10">
              <div className="flex justify-between items-center mb-4">
                <div className="font-semibold text-lg text-blue-700 dark:text-blue-400">
                  Upload Files
                </div>
                <button
                  className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl"
                  onClick={() => setShowDiaryFileUploaderModal(false)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <FileUploader
                model_type="diary"
                model_id={diaryAttachmentsModal.diary.id}
                onFileUpload={async () => {
                  await refreshDiaryAttachments(diaryAttachmentsModal.diary);
                  setShowDiaryFileUploaderModal(false);
                }}
                acceptedFileTypes="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
                maxFileSize={50 * 1024 * 1024}
                multiple={true}
                title=""
              />
            </div>
          </div>
        </Dialog>
      )}

      <ConfirmDeleteDialog
        open={!!entryToDelete}
        title="Delete task"
        name={entryToDelete?.task || "Diary Entry"}
        nameLabel="task name"
        description="The task will no longer be shown. A deleted visit's number or application's letter is used again for the next new one."
        usageUrl={entryToDelete ? `/trials/${trial.id}/diaries/${entryToDelete.id}/usage` : null}
        onDelete={() => api.delete(`/trials/${trial.id}/diaries/${entryToDelete.id}`)}
        onClose={() => setEntryToDelete(null)}
        onDone={() => {
          setEntryToDelete(null);
          fetchTrial();
        }}
      />
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        title="Delete trial"
        name={trial?.trialno}
        nameLabel="trial number"
        description="The trial and its tasks will no longer be shown anywhere in Agromate. Its trial number stays taken."
        onDelete={() => api.delete(`/trials/${trial.id}`)}
        onClose={() => setDeleteDialogOpen(false)}
        onDone={() => navigate(backTo, { replace: true })}
      />
    </div>
  );
}
